import { logout } from './../queries/session';
import { defineStore } from 'pinia';
import { add, isAfter, sub } from 'date-fns';
import { logUserAction } from '~/utils/helpers';

export const useSessionStore = defineStore(
  'session',
  () => {
    const router = useRouter();
    const config = useRuntimeConfig();
    const useUser = useUserStore();

    const defaultSessionDuration = config.public.sessionDuration ? parseInt(config.public.sessionDuration) : 30;

    const isIdle = ref(false);
    const timer = ref(defaultSessionDuration);
    const togglePopup = ref(false);
    const timerActive = ref(false);
    const sessionDuration = ref(defaultSessionDuration);
    const sessionTimeout = ref(add(new Date(), { seconds: sessionDuration.value }).getTime());
    const redirectPath = ref('');
    const setRedirectPath = (value: string) => {
      redirectPath.value = value;
    };

    const setIsIdle = (value: boolean) => {
      isIdle.value = value;
    };

    const resetTimer = (value = defaultSessionDuration) => {
      sessionDuration.value = value;
      sessionTimeout.value = add(new Date(), { seconds: sessionDuration.value }).getTime();
      localStorage.setItem('sessionTimeout', `${sessionTimeout.value}`);
      togglePopup.value = false;
      timer.value = value;
    };

    const extend = async () => {
      const resp = await extendSession();
      if (!resp.success) {
        timeout();
      } else {
        logUserAction({
          action: 'extend_session',
          details: "User's session has been extended",
        })
        resetTimer();
      }
    };

    const timeInterval = ref();
    const startCountdownTimer = () => {
      if (!timeInterval.value) {
        timerActive.value = true;
        timeInterval.value = setInterval(() => {
          timer.value -= 1;
          const now = new Date();
          // const thirtySecondsFromTimeout = sub(sessionTimeout.value, { seconds: 30 });
          const cookieSessionTimeout = parseInt(localStorage.getItem('sessionTimeout') ?? '0');
          const fiveMinsFromTimeout = sub(cookieSessionTimeout, { seconds: 300 });
          if (isAfter(now, fiveMinsFromTimeout)) {
            if (!togglePopup.value) {
              logUserAction({
                action: 'countdown_started',
                details: "User's countdown has started",
              })
            }
            togglePopup.value = true;
          }
        }, 1000);
      }
    };

    const stopCountdownTimer = () => {
      timerActive.value = false;
      togglePopup.value = false;
      clearInterval(timeInterval.value);
      timeInterval.value = null;
    };

    const timeout = async (logout = false) => {
      if (!logout) {
        logUserAction({
          action: 'session_timeout',
          details: "User's session has timed out",
        })
      }
      stopCountdownTimer();
      clearUserSession();
      useUser.clearUser();

      if (config.public.sessionTimeoutRedirectURL.startsWith('/')) {
        await router.push(config.public.sessionTimeoutRedirectURL + `?module=9&redirect_path=${redirectPath.value}`);
        return;
      } else {
        window.location.href = config.public.sessionTimeoutRedirectURL + `?module=9&redirect_path=${redirectPath.value}`;
        return;
      }
    };

    const logoutSession = async () => {
      await logout();
      timeout(true);
    };

    const setTogglePopup = (value: boolean) => {
      togglePopup.value = value;
    };

    const notAuthRedirect = ref(false);
    const setNotAuthRedirect = (value: boolean) => {
      notAuthRedirect.value = value;
    };

    return {
      isIdle,
      setIsIdle,
      togglePopup,
      setTogglePopup,
      timer,
      resetTimer,
      startCountdownTimer,
      stopCountdownTimer,
      timerActive,
      extend,
      timeout,
      sessionTimeout,
      logoutSession,
      redirectPath,
      setRedirectPath,
      notAuthRedirect,
      setNotAuthRedirect,
    };
  },
  {
    persist: true,
  }
);
